import {
  DeviceDemandResponseEvent,
  VehicleDemandResponseEvent,
} from '@/types/deviceDemandResponseEvent'
import { changeVehicleChargingDelayEvent } from '@/actions/vehicleChargingDelayEvents'
import { useAppDispatch } from '@/hooks'
import { chargeNowEventCollection } from '@/reducers/chargeNowEvents'
import { chargeForecastCollection } from '@/reducers/chargeRelatedCollections'
import { vehicleDemandResponseEventsCollection } from '@/reducers/vehicleDemandResponseEvents'
import { CardActionButton, CardActions, Grid, Modal, Text } from '@/components'
import AlertMessage from '@/app/components/AlertMessage'
import useUtilityPrograms from '@/app/hooks/useUtilityPrograms'
import { match } from 'ts-pattern'
import { ChargerEnrollment } from '@/types/programs-new/programs-new'
import { useDeviceEventExplanation } from '@/app/features/vehicleDemandResponseEvent/useEventExplanation'
import { DeviceType } from '@/app/components/DeviceSelectionGallery/DeviceSelectionGallery.types'

type Props = {
  open: boolean
  drEvent: DeviceDemandResponseEvent
  onClose: () => void
  onOptedOut?: () => void
  deviceType: DeviceType
  helpLinkUrl?: string | null
  skipOptOutApiCall?: boolean
}

function useViewModel(props: Props) {
  const dispatch = useAppDispatch()
  const { enrolledDevicePrograms } = useUtilityPrograms({
    onlyMultiPrograms: true,
  })

  const deviceId = match(props.deviceType)
    .with(
      'vehicle',
      () => (props.drEvent as VehicleDemandResponseEvent).vehicle_id,
    )
    .with(
      'vehicleCharger',
      () => (props.drEvent as VehicleDemandResponseEvent).charger_id,
    )
    .otherwise(() => null)

  if (!deviceId) {
    return {
      ...props,
      handleClose: props.onClose,
      onManageEvent: () => {},
      message: 'No device ID found',
      eventOptOutMessage: null,
    }
  }

  const message = useDeviceEventExplanation(deviceId, props.deviceType)

  const invalidateRelatedData = () => {
    dispatch(chargeNowEventCollection.actions.invalidate())
    dispatch(chargeForecastCollection.actions.invalidate())
    dispatch(vehicleDemandResponseEventsCollection.actions.invalidate())
  }

  const onManageEvent = (enrolled: boolean) => {
    const reason = enrolled ? 'opt-in' : 'opt-out'
    props.onClose() // Close the manage modal first

    if (!enrolled && props.skipOptOutApiCall) {
      props.onOptedOut?.()
      return
    }

    dispatch(
      changeVehicleChargingDelayEvent(props.drEvent.id, {
        enrolled,
        reason,
      }),
    )
      .then(invalidateRelatedData)
      .then(() => {
        if (!enrolled) {
          // Only show confirmation modal for opt-out
          props.onOptedOut?.()
        }
      })
  }

  const enrolledProgram = enrolledDevicePrograms.find((program) =>
    match(props.deviceType)
      .with('vehicle', () =>
        program.enrollment_overview?.vehicle_enrollments.some(
          (e) => e.vehicle_id === deviceId,
        ),
      )
      .with('vehicleCharger', () =>
        program.enrollment_overview?.charger_enrollments.some(
          (e: ChargerEnrollment) => e.charger_id === deviceId,
        ),
      )
      .otherwise(() => null),
  )

  const eventOptOutMessage = match(props.deviceType)
    .with(
      'vehicle',
      () => enrolledProgram?.view_config_json?.eventOptOutMessage?.vehicle,
    )
    .with(
      'vehicleCharger',
      () =>
        enrolledProgram?.view_config_json?.eventOptOutMessage?.vehicleCharger,
    )
    .otherwise(() => null)

  return {
    ...props,
    handleClose: props.onClose,
    onManageEvent,
    message,
    eventOptOutMessage,
    helpLinkUrl: props.helpLinkUrl ?? null,
  }
}

function DROptOutModalView({
  open,
  handleClose,
  onManageEvent,
  message,
  eventOptOutMessage,
  drEvent,
  helpLinkUrl,
}: ReturnType<typeof useViewModel>) {
  return (
    <Modal
      id="vehicle-dr-event-details-modal"
      open={open}
      onClose={handleClose}
      title="Manage Event"
    >
      <div className="mb-6">
        <div>
          <AlertMessage variant="info" rawHtmlText={message} />
          {drEvent.enrolled && (
            <Text variant="body4" className="mt-4">
              Are you sure you want to opt out of this event? Doing so may
              reduce your program rewards.
            </Text>
          )}
          {eventOptOutMessage && (
            <Text variant="body4" className="mt-4">
              {eventOptOutMessage}
            </Text>
          )}
          {drEvent.enrolled && helpLinkUrl && (
            <a
              href={helpLinkUrl}
              className="mt-4 block text-blue-600 underline"
              target="_blank"
              rel="noreferrer"
            >
              <Text variant="link">Learn more about DR Events</Text>
            </a>
          )}
        </div>
      </div>
      <Grid flow="row" gap="16px">
        <CardActions>
          {drEvent.enrolled ? (
            <>
              <CardActionButton
                id="vehicle-dr-event-details-opt-out-button"
                type="secondary"
                buttonVariantOverride="primary-subtle"
                onClick={() => onManageEvent(false)}
              >
                Opt Out
              </CardActionButton>
              <CardActionButton
                id="vehicle-dr-event-details-opt-in-button"
                type="primary"
                onClick={handleClose}
              >
                Stay in Event
              </CardActionButton>
            </>
          ) : (
            <>
              <CardActionButton
                id="vehicle-dr-event-details-cancel-button"
                type="secondary"
                onClick={handleClose}
              >
                Cancel
              </CardActionButton>
              <CardActionButton
                id="vehicle-dr-event-details-opt-in-button"
                type="primary"
                onClick={() => onManageEvent(true)}
              >
                Opt In
              </CardActionButton>
            </>
          )}
        </CardActions>
      </Grid>
    </Modal>
  )
}

export default function DROptOutModal(props: Props) {
  const viewModel = useViewModel(props)
  return <DROptOutModalView {...viewModel} />
}
